import React from 'react'
import { Check, ChevronDown, ChevronUp, ChevronsUpDown, Plus, SendHorizonal, X } from 'lucide-react'

import {
  ACCOUNT_TYPE_OPTIONS,
  AUTO_SELECTION,
  NO_SELECTION,
  PRIORITY_OPTIONS,
  PURPOSE_OPTIONS,
  RANDOM_CODE_SELECTION,
  RANDOM_RECEIVER_ACCOUNT_SELECTION,
} from '../../console-constants'
import type { PublishResult, ReceiverAccount, RejectionCodeInfo, ScenarioInfo } from '../../console-types'
import type { BacenIspbEntry } from '../../../bacen-ispb-cache'
import { cn } from '../../lib/utils'
import { api } from '../../lib/api'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { ResultBlock } from './shared'

/* ─── Checkbox ──────────────────────────────────────────────────── */
function Checkbox({ checked, onToggle, id }: { checked: boolean; onToggle: () => void; id?: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      id={id}
      onClick={onToggle}
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded border transition-all',
        checked
          ? 'border-primary bg-primary'
          : 'border-border/50 bg-transparent hover:border-primary/50'
      )}
    >
      {checked && <Check className="size-2.5 text-primary-foreground" strokeWidth={3} />}
    </button>
  )
}

/* ─── SpecifyField ──────────────────────────────────────────────── */
function SpecifyField({
  name, label, placeholder, mono = false, specified, onToggle, type = 'text', list,
}: {
  name: string; label: string; placeholder?: string; mono?: boolean
  specified: boolean; onToggle: () => void; type?: string; list?: string
}) {
  const id = `spec-${name}`
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Checkbox checked={specified} onToggle={onToggle} id={id} />
        <label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-[11px] font-semibold uppercase tracking-wider transition-colors',
            specified ? 'text-foreground/80' : 'text-muted-foreground/50'
          )}
        >
          {label}
        </label>
      </div>
      {specified ? (
        <Input
          type={type}
          name={name}
          placeholder={placeholder}
          list={list}
          className={cn('h-9 bg-input text-sm', mono && 'font-mono text-xs')}
          autoComplete="off"
        />
      ) : (
        <div className="flex h-9 cursor-default select-none items-center rounded-md border border-border/20 bg-muted/10 px-3 font-mono text-xs text-muted-foreground/25">
          auto
        </div>
      )}
    </div>
  )
}

/* ─── SpecifySelectField ────────────────────────────────────────── */
function SpecifySelectField({
  name, label, value, onValueChange, options, defaultWhenSpecified, submitValue,
}: {
  name: string; label: string; value: string; onValueChange: (v: string) => void
  options: Array<{ value: string; label: string }>; defaultWhenSpecified: string; submitValue?: string
}) {
  const specified = value !== AUTO_SELECTION
  const id = `spec-${name}`
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Checkbox
          checked={specified}
          onToggle={() => onValueChange(specified ? AUTO_SELECTION : defaultWhenSpecified)}
          id={id}
        />
        <label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-[11px] font-semibold uppercase tracking-wider transition-colors',
            specified ? 'text-foreground/80' : 'text-muted-foreground/50'
          )}
        >
          {label}
        </label>
      </div>
      {specified ? (
        <>
          <input type="hidden" name={name} value={submitValue ?? value} />
          <Select value={value || undefined} onValueChange={onValueChange}>
            <SelectTrigger className="h-9 bg-input text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.filter((o) => o.value !== AUTO_SELECTION).map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      ) : (
        <div className="flex h-9 cursor-default select-none items-center rounded-md border border-border/20 bg-muted/10 px-3 font-mono text-xs text-muted-foreground/25">
          auto
        </div>
      )}
    </div>
  )
}

/* ─── IspbCombobox ──────────────────────────────────────────────── */
function IspbCombobox({
  value,
  onChange,
  entries,
}: {
  value: string
  onChange: (ispb: string) => void
  entries: BacenIspbEntry[]
}) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => searchRef.current?.focus(), 10)
    return () => clearTimeout(timer)
  }, [open])

  React.useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selectedEntry = entries.find((e) => e.ispb === value)
  const filtered = React.useMemo(() => {
    if (!search.trim()) return entries.slice(0, 100)
    const q = search.toLowerCase()
    return entries.filter((e) => e.name.toLowerCase().includes(q) || e.ispb.includes(q)).slice(0, 100)
  }, [entries, search])

  function select(entry: BacenIspbEntry) {
    onChange(entry.ispb)
    setOpen(false)
    setSearch('')
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border bg-input px-3 text-sm transition-colors',
          open ? 'border-ring/50 ring-1 ring-ring/20' : 'border-border hover:border-border/80'
        )}
      >
        {selectedEntry ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground/60 shrink-0">{selectedEntry.ispb}</span>
            <span className="truncate text-xs">{selectedEntry.name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground/40 text-sm">Selecionar banco...</span>
        )}
        <ChevronsUpDown className="ml-2 size-3.5 shrink-0 text-muted-foreground/40" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[280px] rounded-md border border-border bg-popover shadow-lg">
          <div className="border-b border-border/50 p-2">
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar banco ou ISPB..."
              className="h-7 w-full rounded border border-border/50 bg-input px-2 text-xs outline-none placeholder:text-muted-foreground/40 focus:border-ring/50"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); setSearch('') }}
                className="flex w-full items-center gap-2 border-b border-border/30 px-3 py-1.5 text-xs text-muted-foreground/50 hover:bg-secondary/50"
              >
                <X className="size-3" /> Limpar seleção
              </button>
            )}
            {filtered.map((entry) => (
              <button
                key={entry.ispb}
                type="button"
                onClick={() => select(entry)}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-secondary/50',
                  value === entry.ispb && 'bg-primary/10'
                )}
              >
                <span className="w-20 shrink-0 font-mono text-[11px] text-muted-foreground/50">{entry.ispb}</span>
                <span className={cn('truncate text-xs', value === entry.ispb ? 'text-primary' : 'text-foreground/80')}>
                  {entry.name}
                </span>
                {value === entry.ispb && <Check className="ml-auto size-3 shrink-0 text-primary" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-center font-mono text-xs text-muted-foreground/30">Nenhum resultado</p>
            )}
          </div>
          {!search && entries.length > 100 && (
            <p className="border-t border-border/30 px-3 py-1.5 font-mono text-[10px] text-muted-foreground/30">
              {entries.length} participantes · pesquise para filtrar
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── SpecifyIspbField ──────────────────────────────────────────── */
function SpecifyIspbField({
  name, label, value, onChange, entries, specified, onToggle,
}: {
  name: string; label: string; value: string; onChange: (v: string) => void
  entries: BacenIspbEntry[]; specified: boolean; onToggle: () => void
}) {
  const id = `spec-${name}`
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Checkbox checked={specified} onToggle={onToggle} id={id} />
        <label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-[11px] font-semibold uppercase tracking-wider transition-colors',
            specified ? 'text-foreground/80' : 'text-muted-foreground/50'
          )}
        >
          {label}
        </label>
      </div>
      {specified ? (
        <>
          <input type="hidden" name={name} value={value} />
          <IspbCombobox value={value} onChange={onChange} entries={entries} />
        </>
      ) : (
        <div className="flex h-9 cursor-default select-none items-center rounded-md border border-border/20 bg-muted/10 px-3 font-mono text-xs text-muted-foreground/25">
          auto
        </div>
      )}
    </div>
  )
}

/* ─── SearchableCombobox ────────────────────────────────────────── */
function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder = 'Selecionar...',
}: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string; sub?: string }[]
  placeholder?: string
}) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => searchRef.current?.focus(), 10)
    return () => clearTimeout(timer)
  }, [open])

  React.useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selected = options.find((o) => o.value === value)
  const filtered = React.useMemo(() => {
    if (!search.trim()) return options
    const q = search.toLowerCase()
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.sub?.toLowerCase().includes(q) ?? false)
    )
  }, [options, search])

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border bg-input px-3 text-sm transition-colors',
          open ? 'border-ring/50 ring-1 ring-ring/20' : 'border-border hover:border-border/80'
        )}
      >
        {selected ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="font-mono text-xs font-semibold shrink-0">{selected.label}</span>
            {selected.sub && <span className="truncate text-xs text-muted-foreground/60">{selected.sub}</span>}
          </span>
        ) : (
          <span className="text-muted-foreground/40 text-sm">{placeholder}</span>
        )}
        <ChevronsUpDown className="ml-2 size-3.5 shrink-0 text-muted-foreground/40" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[280px] rounded-md border border-border bg-popover shadow-lg">
          <div className="border-b border-border/50 p-2">
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="h-7 w-full rounded border border-border/50 bg-input px-2 text-xs outline-none placeholder:text-muted-foreground/40 focus:border-ring/50"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); setSearch('') }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-secondary/50',
                  value === opt.value && 'bg-primary/10'
                )}
              >
                <span className={cn('font-mono text-xs font-semibold shrink-0', value === opt.value ? 'text-primary' : 'text-foreground/80')}>
                  {opt.label}
                </span>
                {opt.sub && <span className="truncate text-xs text-muted-foreground/50">{opt.sub}</span>}
                {value === opt.value && <Check className="ml-auto size-3 shrink-0 text-primary" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-center font-mono text-xs text-muted-foreground/30">Nenhum resultado</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── SectionLabel ──────────────────────────────────────────────── */
function SectionLabel({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {children}
      </span>
      <div className="h-px flex-1 bg-border/40" />
      {action}
    </div>
  )
}

/* ─── AddAccountForm ────────────────────────────────────────────── */
function AddAccountForm({
  ispbEntries,
  onSave,
  onCancel,
}: {
  ispbEntries: BacenIspbEntry[]
  onSave: (account: ReceiverAccount) => void
  onCancel: () => void
}) {
  const [form, setForm] = React.useState<ReceiverAccount>({ conta: '', tipoConta: 'CACC' })
  const set = (k: keyof ReceiverAccount) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }))

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <p className="mb-3 font-display text-[10px] font-bold uppercase tracking-widest text-primary/70">
        Nova Conta
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
            Número da Conta <span className="text-primary">*</span>
          </label>
          <Input
            value={form.conta}
            onChange={set('conta')}
            placeholder="991199"
            className="h-9 bg-input font-mono text-sm"
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Nome
          </label>
          <Input value={form.nome ?? ''} onChange={set('nome')} placeholder="Opcional" className="h-9 bg-input text-sm" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            CPF / CNPJ
          </label>
          <Input value={form.cpfCnpj ?? ''} onChange={set('cpfCnpj')} placeholder="Opcional" className="h-9 bg-input font-mono text-sm" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Agência
          </label>
          <Input value={form.agencia ?? ''} onChange={set('agencia')} placeholder="0001" className="h-9 bg-input font-mono text-sm" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            ISPB
          </label>
          <IspbCombobox
            value={form.ispb ?? ''}
            onChange={(ispb) => setForm((p) => ({ ...p, ispb }))}
            entries={ispbEntries}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Tipo Conta
          </label>
          <Select
            value={form.tipoConta ?? 'CACC'}
            onValueChange={(v) => setForm((p) => ({ ...p, tipoConta: v as 'CACC' | 'SVGS' }))}
          >
            <SelectTrigger className="h-9 bg-input text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CACC">CACC</SelectItem>
              <SelectItem value="SVGS">SVGS</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!form.conta.trim()}
          onClick={() => { if (form.conta.trim()) onSave({ ...form, conta: form.conta.trim() }) }}
          className="h-8 gap-1.5"
        >
          <Check className="size-3.5" />
          Salvar
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="h-8">
          Cancelar
        </Button>
      </div>
    </div>
  )
}

/* ─── PublishTab ────────────────────────────────────────────────── */
export function PublishTab({
  scenarios,
  selectedScenarios,
  setSelectedScenarios,
  expectedResponse,
  setExpectedResponse,
  expectedRejectCode,
  setExpectedRejectCode,
  rejectionCodes,
  payerAccountType,
  setPayerAccountType,
  receiverAccountSelection,
  setReceiverAccountSelection,
  receiverAccounts,
  setReceiverAccounts,
  transactionPurpose,
  setTransactionPurpose,
  transactionPriority,
  setTransactionPriority,
  isPublishing,
  publishResult,
  onSubmit,
}: {
  scenarios: ScenarioInfo[]
  selectedScenarios: string[]
  setSelectedScenarios: (v: string[]) => void
  expectedResponse: string
  setExpectedResponse: (v: string) => void
  expectedRejectCode: string
  setExpectedRejectCode: (v: string) => void
  rejectionCodes: RejectionCodeInfo[]
  payerAccountType: string
  setPayerAccountType: (v: string) => void
  receiverAccountSelection: string
  setReceiverAccountSelection: (v: string) => void
  receiverAccounts: ReceiverAccount[]
  setReceiverAccounts: (accounts: ReceiverAccount[]) => void
  transactionPurpose: string
  setTransactionPurpose: (v: string) => void
  transactionPriority: string
  setTransactionPriority: (v: string) => void
  isPublishing: boolean
  publishResult: PublishResult | null
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
}) {
  const [specified, setSpecified] = React.useState<Record<string, boolean>>({})
  const [showAdvanced, setShowAdvanced] = React.useState(false)
  const [addingAccount, setAddingAccount] = React.useState(false)
  const [ispbEntries, setIspbEntries] = React.useState<BacenIspbEntry[]>([])
  const [payerIspb, setPayerIspb] = React.useState('')

  const isSpec = (k: string) => !!specified[k]
  const toggle = (k: string) => setSpecified((p) => ({ ...p, [k]: !p[k] }))

  // Load BACEN ISPB list once
  React.useEffect(() => {
    api<{ ispbs: BacenIspbEntry[] }>('GET', '/_mock/ispbs')
      .then(({ data }) => { if (data?.ispbs?.length) setIspbEntries(data.ispbs) })
      .catch(() => {})
  }, [])

  /* ── scenario checklist ── */
  // Exclude RANDOMICO — the checklist IS the randomization mechanism
  const checklistScenarios = scenarios.filter((s) => s.cenario !== 'RANDOMICO')

  function toggleScenario(cenario: string) {
    setSelectedScenarios(
      selectedScenarios.includes(cenario)
        ? selectedScenarios.filter((s) => s !== cenario)
        : [...selectedScenarios, cenario]
    )
  }

  function selectAllScenarios() {
    setSelectedScenarios(checklistScenarios.map((s) => s.cenario))
  }

  function clearAllScenarios() {
    setSelectedScenarios([])
  }

  /* ── receiver account helpers ── */
  function addAccount(account: ReceiverAccount) {
    if (receiverAccounts.some((a) => a.conta === account.conta)) return
    setReceiverAccounts([...receiverAccounts, account])
    setAddingAccount(false)
  }

  function removeAccount(conta: string) {
    setReceiverAccounts(receiverAccounts.filter((a) => a.conta !== conta))
    if (receiverAccountSelection === conta) {
      setReceiverAccountSelection(RANDOM_RECEIVER_ACCOUNT_SELECTION)
    }
  }

  function toggleAccountSelection(conta: string) {
    setReceiverAccountSelection(
      receiverAccountSelection === conta ? RANDOM_RECEIVER_ACCOUNT_SELECTION : conta
    )
  }

  /* ── rejection options ── */
  const rejectionOptions = [
    { value: RANDOM_CODE_SELECTION, label: 'Aleatório', sub: undefined },
    ...rejectionCodes.map((c) => ({ value: c.codigo, label: c.codigo, sub: c.descricao })),
  ]

  const selectedScenarioInfo =
    selectedScenarios.length === 1
      ? scenarios.find((s) => s.cenario === selectedScenarios[0])
      : null

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">Publicar pacs.008</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Campos desmarcados são gerados aleatoriamente pelo mock.
        </p>
      </div>

      <form onSubmit={onSubmit} autoComplete="off" className="space-y-6">

        {/* ── CENÁRIO ──────────────────────────────────────────── */}
        <div className="space-y-3">
          <SectionLabel
            action={
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllScenarios}
                  className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                >
                  Todos
                </button>
                <span className="text-muted-foreground/30">·</span>
                <button
                  type="button"
                  onClick={clearAllScenarios}
                  className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                >
                  Nenhum
                </button>
              </div>
            }
          >
            Cenário
          </SectionLabel>

          <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {checklistScenarios.map((scenario) => {
              const checked = selectedScenarios.includes(scenario.cenario)
              return (
                <button
                  key={scenario.cenario}
                  type="button"
                  onClick={() => toggleScenario(scenario.cenario)}
                  className={cn(
                    'flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-left transition-colors',
                    checked
                      ? 'border-primary/40 bg-primary/8'
                      : 'border-border/40 bg-card/40 hover:border-border/60 hover:bg-card/60'
                  )}
                >
                  <div className={cn(
                    'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border transition-all',
                    checked ? 'border-primary bg-primary' : 'border-border/50'
                  )}>
                    {checked && <Check className="size-2.5 text-primary-foreground" strokeWidth={3} />}
                  </div>
                  <div>
                    <div className={cn(
                      'font-mono text-xs font-semibold',
                      checked ? 'text-primary' : 'text-foreground/70'
                    )}>
                      {scenario.cenario}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground/60">
                      {scenario.descricao}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {selectedScenarios.length === 0 && (
            <p className="text-xs text-red-400/70">Selecione ao menos um cenário.</p>
          )}
          {selectedScenarios.length > 1 && (
            <p className="font-mono text-xs text-muted-foreground/50">
              {selectedScenarios.length} cenários no pool — um será sorteado por transação
            </p>
          )}
          {selectedScenarioInfo && selectedScenarioInfo.regras.length > 0 && (
            <div className="rounded-md border border-border/40 bg-muted/20 px-3 py-2">
              <ul className="space-y-0.5">
                {selectedScenarioInfo.regras.map((rule) => (
                  <li key={rule} className="flex items-start gap-2 text-xs text-muted-foreground/60">
                    <span className="mt-0.5 font-mono text-muted-foreground/30">›</span>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── ENVIO ────────────────────────────────────────────── */}
        <div className="space-y-3">
          <SectionLabel>Envio</SectionLabel>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
                Quantidade
              </span>
              <Input type="number" name="quantidade" defaultValue="1" min="1" max="50" className="h-9 bg-input text-sm" />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
                Delay (ms)
              </span>
              <Input type="number" name="delayEntreMensagensMs" defaultValue="0" min="0" max="60000" className="h-9 bg-input text-sm" />
            </div>
            <SpecifyField
              name="valor"
              label="Valor (R$)"
              placeholder="R$10 – R$5.000"
              specified={isSpec('valor')}
              onToggle={() => toggle('valor')}
            />
          </div>
        </div>

        {/* ── RESPOSTA ESPERADA ─────────────────────────────────── */}
        <div className="space-y-3">
          <SectionLabel>Resposta Esperada</SectionLabel>
          <input type="hidden" name="respostaEsperada" value={expectedResponse === NO_SELECTION ? '' : expectedResponse} />
          <div className="grid gap-1.5 grid-cols-3">
            {([
              { value: NO_SELECTION, label: 'Manual', desc: 'sem auto-resposta' },
              { value: 'ACSP', label: 'ACSP', desc: 'auto confirma' },
              { value: 'RJCT', label: 'RJCT', desc: 'auto rejeita' },
            ] as const).map((opt) => {
              const active = expectedResponse === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setExpectedResponse(opt.value); if (opt.value !== 'RJCT') setExpectedRejectCode(RANDOM_CODE_SELECTION) }}
                  className={cn(
                    'flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-left transition-colors',
                    active
                      ? 'border-primary/40 bg-primary/8'
                      : 'border-border/40 bg-card/40 hover:border-border/60 hover:bg-card/60'
                  )}
                >
                  <div className={cn(
                    'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-all',
                    active ? 'border-primary' : 'border-border/50'
                  )}>
                    {active && <div className="size-1.5 rounded-full bg-primary" />}
                  </div>
                  <div>
                    <div className={cn('font-mono text-xs font-semibold', active ? 'text-primary' : 'text-foreground/70')}>
                      {opt.label}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground/60">{opt.desc}</div>
                  </div>
                </button>
              )
            })}
          </div>
          {expectedResponse === 'RJCT' && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">Código de Erro</span>
              <input type="hidden" name="codigoDeErroEsperado" value={expectedRejectCode === RANDOM_CODE_SELECTION ? '' : expectedRejectCode} />
              <SearchableCombobox
                value={expectedRejectCode}
                onChange={setExpectedRejectCode}
                options={rejectionOptions}
                placeholder="Aleatório..."
              />
            </div>
          )}
        </div>

        {/* ── PAGADOR ───────────────────────────────────────────── */}
        <div className="space-y-3">
          <SectionLabel>Pagador</SectionLabel>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SpecifyField name="nomeUsuarioPagador" label="Nome" specified={isSpec('nomeUsuarioPagador')} onToggle={() => toggle('nomeUsuarioPagador')} />
            <SpecifyField name="cpfCnpjUsuarioPagador" label="CPF / CNPJ" mono specified={isSpec('cpfCnpjUsuarioPagador')} onToggle={() => toggle('cpfCnpjUsuarioPagador')} />
            <SpecifyField name="contaUsuarioPagador" label="Conta" mono specified={isSpec('contaUsuarioPagador')} onToggle={() => toggle('contaUsuarioPagador')} />
            <SpecifyField name="agenciaUsuarioPagador" label="Agência" mono specified={isSpec('agenciaUsuarioPagador')} onToggle={() => toggle('agenciaUsuarioPagador')} />
            <SpecifySelectField
              name="tipoContaUsuarioPagador"
              label="Tipo Conta"
              value={payerAccountType}
              onValueChange={setPayerAccountType}
              options={ACCOUNT_TYPE_OPTIONS}
              defaultWhenSpecified="CACC"
              submitValue={payerAccountType === AUTO_SELECTION ? '' : payerAccountType}
            />
            <SpecifyIspbField
              name="participanteDoUsuarioPagador"
              label="ISPB"
              value={payerIspb}
              onChange={setPayerIspb}
              entries={ispbEntries}
              specified={isSpec('participanteDoUsuarioPagador')}
              onToggle={() => { toggle('participanteDoUsuarioPagador'); if (isSpec('participanteDoUsuarioPagador')) setPayerIspb('') }}
            />
          </div>
        </div>

        {/* ── RECEBEDOR ─────────────────────────────────────────── */}
        <div className="space-y-3">
          <SectionLabel
            action={
              !addingAccount && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setAddingAccount(true)}
                  className="h-6 gap-1.5 px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 hover:text-foreground"
                >
                  <Plus className="size-3" />
                  Adicionar
                </Button>
              )
            }
          >
            Contas de Destino
          </SectionLabel>

          {/* Add account form */}
          {addingAccount && (
            <AddAccountForm
              ispbEntries={ispbEntries}
              onSave={addAccount}
              onCancel={() => setAddingAccount(false)}
            />
          )}

          {/* Account list */}
          {receiverAccounts.length === 0 && !addingAccount && (
            <div className="rounded-md border border-border/30 bg-card/30 px-4 py-5 text-center">
              <p className="font-mono text-xs text-muted-foreground/40">Nenhuma conta configurada.</p>
              <button
                type="button"
                onClick={() => setAddingAccount(true)}
                className="mt-2 text-xs text-primary/60 hover:text-primary transition-colors underline-offset-2 hover:underline"
              >
                Adicionar conta
              </button>
            </div>
          )}

          {receiverAccounts.length > 0 && (
            <div className="space-y-2">
              {receiverAccounts.map((account) => {
                const isSelected = receiverAccountSelection === account.conta
                return (
                  <div
                    key={account.conta}
                    className={cn(
                      'group flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
                      isSelected
                        ? 'border-primary/40 bg-primary/8'
                        : 'border-border/40 bg-card/40 hover:border-border/60'
                    )}
                  >
                    {/* Select toggle */}
                    <button
                      type="button"
                      onClick={() => toggleAccountSelection(account.conta)}
                      className="mt-0.5 shrink-0"
                      title={isSelected ? 'Desfixar conta' : 'Fixar esta conta'}
                    >
                      <div className={cn(
                        'flex size-4 items-center justify-center rounded-full border-2 transition-all',
                        isSelected ? 'border-primary' : 'border-border/50 hover:border-primary/50'
                      )}>
                        {isSelected && <div className="size-2 rounded-full bg-primary" />}
                      </div>
                    </button>

                    {/* Account data */}
                    <div className="flex-1 min-w-0">
                      <div className={cn(
                        'font-mono text-sm font-semibold',
                        isSelected ? 'text-primary' : 'text-foreground/80'
                      )}>
                        {account.conta}
                        {isSelected && (
                          <span className="ml-2 font-display text-[10px] font-bold uppercase tracking-widest text-primary/60">
                            selecionada
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {account.nome && (
                          <span className="text-xs text-muted-foreground/70">{account.nome}</span>
                        )}
                        {account.cpfCnpj && (
                          <span className="font-mono text-xs text-muted-foreground/50">
                            {account.cpfCnpj}
                          </span>
                        )}
                        {account.agencia && (
                          <span className="font-mono text-xs text-muted-foreground/50">
                            Ag: {account.agencia}
                          </span>
                        )}
                        {account.ispb && (
                          <span className="font-mono text-xs text-muted-foreground/50">
                            ISPB: {account.ispb}
                          </span>
                        )}
                        {account.tipoConta && (
                          <span className="font-mono text-xs text-muted-foreground/40">
                            {account.tipoConta}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => removeAccount(account.conta)}
                      className="shrink-0 rounded p-0.5 text-muted-foreground/30 transition-colors hover:text-red-400 opacity-0 group-hover:opacity-100"
                      title={`Remover ${account.conta}`}
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                )
              })}

              {receiverAccounts.length > 1 && receiverAccountSelection === RANDOM_RECEIVER_ACCOUNT_SELECTION && (
                <p className="font-mono text-[10px] text-muted-foreground/40">
                  Nenhuma selecionada — conta aleatória da lista por transação
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── AVANÇADOS ─────────────────────────────────────────── */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-muted-foreground"
          >
            {showAdvanced ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
            Campos Avançados
          </button>

          {showAdvanced && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <SpecifyField name="informacoesEntreUsuarios" label="Info entre Usuários" specified={isSpec('informacoesEntreUsuarios')} onToggle={() => toggle('informacoesEntreUsuarios')} />
                <SpecifySelectField
                  name="finalidadeDaTransacao"
                  label="Finalidade"
                  value={transactionPurpose}
                  onValueChange={setTransactionPurpose}
                  options={PURPOSE_OPTIONS}
                  defaultWhenSpecified="IPAY"
                  submitValue={transactionPurpose === AUTO_SELECTION ? '' : transactionPurpose}
                />
                <SpecifySelectField
                  name="tipoPrioridadePagamento"
                  label="Prioridade"
                  value={transactionPriority}
                  onValueChange={setTransactionPriority}
                  options={PRIORITY_OPTIONS}
                  defaultWhenSpecified="PAGPRI"
                  submitValue={transactionPriority === AUTO_SELECTION ? '' : transactionPriority}
                />
                <SpecifyField name="endToEndID" label="EndToEndID" placeholder="Auto-gerado" mono specified={isSpec('endToEndID')} onToggle={() => toggle('endToEndID')} />
                <SpecifyField name="idConciliacaoRecebedor" label="ID Conciliação (TxId)" placeholder="Auto (QRDN)" mono specified={isSpec('idConciliacaoRecebedor')} onToggle={() => toggle('idConciliacaoRecebedor')} />
                <SpecifyField name="idContaTransacional" label="Chave PIX" placeholder="Auto (DICT/QRES/QRDN)" mono specified={isSpec('idContaTransacional')} onToggle={() => toggle('idContaTransacional')} />
                <SpecifyField name="cnpjIniciadorPagamento" label="CNPJ Iniciador" placeholder="Auto (INIC)" mono specified={isSpec('cnpjIniciadorPagamento')} onToggle={() => toggle('cnpjIniciadorPagamento')} />
                <SpecifyField name="modalidadeAgente" label="Modalidade Agente" specified={isSpec('modalidadeAgente')} onToggle={() => toggle('modalidadeAgente')} />
                <SpecifyField name="prestadorDoServicoDeSaque" label="Prestador de Saque" specified={isSpec('prestadorDoServicoDeSaque')} onToggle={() => toggle('prestadorDoServicoDeSaque')} />
                <SpecifyField name="recebimento" label="Recebimento" specified={isSpec('recebimento')} onToggle={() => toggle('recebimento')} />
              </div>
            </div>
          )}
        </div>

        {/* ── SUBMIT ───────────────────────────────────────────── */}
        <div className="border-t border-border/40 pt-4">
          <Button
            type="submit"
            disabled={isPublishing || selectedScenarios.length === 0 || receiverAccounts.length === 0}
            className="gap-2"
          >
            <SendHorizonal className="size-4" />
            {isPublishing ? 'Publicando...' : 'Publicar Transação'}
          </Button>
        </div>
      </form>

      {publishResult && (
        <div>
          <ResultBlock result={publishResult} />
        </div>
      )}
    </div>
  )
}
